/**
 * Indicatieve Ug-berekening (glas-U-waarde, center-of-pane) — vereenvoudigde EN 673.
 * Dit is de GEDEELDE fysica: de U-waarde-calculator én de isolatieglas-configurator rekenen er
 * allebei mee, zodat ze exact hetzelfde resultaat geven. De spouwweerstand volgt uit straling
 * (emissiviteit van de coating) + geleiding/convectie (gas + spouwbreedte via het Nusselt-getal):
 * een bredere spouw isoleert beter tot ~16 mm; daarboven neemt convectie de winst weer licht af.
 * Bewust indicatief — geen gecertificeerde productwaarde.
 */
import { BENG_PRODUCTS, bengSpecsFor } from "@glastotaal/core";

export type Gas = "argon" | "krypton" | "lucht";

export interface UgInput {
  /** Aantal ruiten: 1 = enkel, 2 = dubbel, 3 = triple. */
  panes: 1 | 2 | 3;
  /** HR-coating (low-E) aanwezig — per spouw één coating. */
  lowE: boolean;
  gas: Gas;
  /** Spouwbreedte in mm (per spouw). */
  widthMm: number;
}

const E_UNCOATED = 0.837; // emissiviteit blank floatglas
const GAS_PROPS: Record<Gas, { lambda: number; rho: number; mu: number; cp: number }> = {
  lucht: { lambda: 0.02496, rho: 1.232, mu: 1.761e-5, cp: 1008 },
  argon: { lambda: 0.01684, rho: 1.699, mu: 2.164e-5, cp: 519 },
  krypton: { lambda: 0.00900, rho: 3.560, mu: 2.480e-5, cp: 248 },
};

/** Warmteweerstand van één spouw (m²K/W): straling (coating) + geleiding/convectie (gas + breedte). */
export function cavityResistance(widthMm: number, gas: Gas, lowE: number | null): number {
  const d = Math.max(widthMm, 4) / 1000;
  const Tm = 283; // K
  const eEff = 1 / (1 / (lowE ?? E_UNCOATED) + 1 / E_UNCOATED - 1);
  const hr = 4 * 5.67e-8 * Tm ** 3 * eEff; // stralingsgeleiding
  const g = GAS_PROPS[gas];
  const Gr = (9.81 * (1 / Tm) * d ** 3 * 15 * g.rho ** 2) / g.mu ** 2;
  const Ra = Gr * ((g.mu * g.cp) / g.lambda);
  const Nu = Math.max(1, 0.035 * Ra ** 0.38); // 1 = pure geleiding; >1 = convectie
  const hg = (Nu * g.lambda) / d;
  return 1 / (hr + hg);
}

/** Ruwe Ug (W/m²K), niet afgerond. `coatedCavities` = aantal spouwen met een low-E coating. */
export function modelUg(opts: { panes: number; coatedCavities: number; gas: Gas; widthMm: number; lowE?: number }): number {
  const nCav = opts.panes - 1;
  if (nCav <= 0) return 5.8; // enkel glas
  let R = 0.04 + 0.13 + 0.005 * opts.panes; // Rse + Rsi + glasbladen
  for (let i = 0; i < nCav; i++) R += cavityResistance(opts.widthMm, opts.gas, i < opts.coatedCavities ? (opts.lowE ?? 0.04) : null);
  return 1 / R;
}

/** Indicatieve Ug (W/m²K) voor de calculator — 2 decimalen. */
export function calcUg(i: UgInput): number {
  if (i.panes === 1) return 5.8;
  const ncav = i.panes - 1;
  return Math.round(modelUg({ panes: i.panes, coatedCavities: i.lowE ? ncav : 0, gas: i.gas, widthMm: i.widthMm }) * 100) / 100;
}

/** Kwalitatief label + kleur bij een Ug-waarde. */
export function ugRating(ug: number): { label: string; tone: "buy" | "primary" | "accent" } {
  if (ug <= 0.8) return { label: "Topisolatie", tone: "buy" };
  if (ug <= 1.2) return { label: "Zeer goed (HR++)", tone: "buy" };
  if (ug <= 1.8) return { label: "Redelijk", tone: "accent" };
  return { label: "Matige isolatie", tone: "accent" };
}

/** Dichtstbijzijnde herkenbare opbouw, voor context + link naar de configurator. */
export function nearestPakket(i: UgInput): string {
  if (i.panes === 1) return "Enkel glas";
  if (i.panes === 3) return "Triple glas (HR+++)";
  return i.lowE ? "HR++ dubbelglas" : "Standaard dubbelglas (geen coating)";
}

/** Beste (laagste) U-waarde die het BENGglas-assortiment haalt — uit de catalogus, zodat de
 *  calculator en de vacuümglaspagina niet uit elkaar lopen als de prijslijst wijzigt. */
export const BENG_BEST_UG = Math.min(...BENG_PRODUCTS.map((p) => bengSpecsFor(p).uValue));

/** Referentiewaarden voor de vergelijkbalk (vacuümglas = beste optie). */
export const UG_REFERENCES: { label: string; ug: number }[] = [
  { label: "Enkel glas", ug: 5.8 },
  { label: "Dubbel glas (oud)", ug: 2.8 },
  { label: "HR++", ug: 1.1 },
  { label: "Triple (HR+++)", ug: 0.6 },
  { label: "Vacuümglas (BENGglas)", ug: BENG_BEST_UG },
];
